"use client";

import { useRef, useCallback } from "react";
import { Check, LoaderCircle, AlertCircle, Circle } from "lucide-react";
import { DATA_KEYS, useRemoteData } from "@/utils/useRemoteData";
import ImageWithFallback from "@/components/ImageWithFallback";
import { NoxusIcon } from "@/components/UI/NoxusIcon";
import { nodeIconName } from "@/components/SpotflowV2/subflowIcon";
import { BaseTool, ToolPartBase, ToolState } from "./ToolComponents";
import type { ToolDisplayInfo } from "./toolDisplay";
import { Run } from "@/generated";
import { useChatDisplayOptionsContext } from "./useChatDisplayOptions";
import type { NoxusDataTypes } from "./noxus-message-types";

type WorkflowRunToolProps = {
  metadata?: NoxusDataTypes["run_workflow"];
  state: string;
  input: unknown;
  output: unknown;
  groupId: string;
  toolCallId: string;
  isPublic?: boolean;
  callProviderMetadata?: {
    noxus?: {
      error?: boolean;
      error_message?: string;
    };
  };
};

type WorkflowProgressProps = {
  runData: Run | undefined;
};

function WorkflowProgress({ runData }: WorkflowProgressProps) {
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const { data: nodesData } = useRemoteData(DATA_KEYS.NODES);

  const run = runData;

  const getNodeById = (nodeId: string) => {
    if (!run?.workflow_definition?.nodes) return null;
    const nodes = Array.isArray(run.workflow_definition.nodes)
      ? run.workflow_definition.nodes
      : [];
    return nodes.find((node: any) => node.id === nodeId);
  };

  const getNodeData = useCallback(
    (nodeType: string) => {
      return nodesData?.find((node: any) => node.type === nodeType);
    },
    [nodesData]
  );

  const getNodeStatus = (nodeId: string) => {
    if (!run?.progress_details?.per_node_progress) return "unknown";
    const nodeProgress = run.progress_details.per_node_progress[nodeId];
    return nodeProgress?.status || "unknown";
  };

  const getNodeStatusIcon = (status: string) => {
    switch (status) {
      case "succeeded":
        return <Check className="h-3.5 w-3.5 text-success-600" />;
      case "running":
        return (
          <LoaderCircle className="h-3.5 w-3.5 animate-spin text-blue-500" />
        );
      case "errored":
        return <AlertCircle className="h-3.5 w-3.5 text-red-500" />;
      default:
        return <Circle className="h-2 w-2 text-gray-400" />;
    }
  };

  const progressDetails = run?.progress_details || {
    executed_nodes: [],
    running_nodes: [],
    remaining_nodes: [],
    skipped_nodes: [],
  };

  const allNodes = [
    ...(progressDetails.executed_nodes || []),
    ...(progressDetails.running_nodes || []),
    ...(progressDetails.remaining_nodes || []),
    ...(progressDetails.skipped_nodes || []),
  ];

  const filteredNodes = allNodes.filter((nodeId) => {
    const node = getNodeById(nodeId);
    return (
      node &&
      node.type !== "InputNode" &&
      node.type !== "OutputNode" &&
      node.type !== "StickyNoteNode"
    );
  });

  if (filteredNodes.length === 0) {
    return (
      <div className="px-6 py-3 text-center">
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <LoaderCircle className="h-4 w-4 animate-spin" />
          <span>Preparing workflow...</span>
        </div>
      </div>
    );
  }

  return (
    <div
      ref={scrollContainerRef}
      className="px-3 py-2 space-y-1 max-h-64 overflow-y-auto"
    >
      {filteredNodes.map((nodeId) => {
        const node = getNodeById(nodeId);
        const nodeStatus = getNodeStatus(nodeId);

        if (!node) return null;

        const nodeData = getNodeData(node.type);
        const nodeImage = nodeData?.image;
        const iconName = nodeIconName(node.type, node.node_config);

        return (
          <div
            key={nodeId}
            className="flex items-center gap-2.5 p-1.5 rounded-md hover:bg-muted/50 transition-colors"
          >
            <div className="shrink-0">
              <div className="flex h-5 w-5 items-center justify-center">
                {getNodeStatusIcon(nodeStatus)}
              </div>
            </div>
            {iconName ? (
              <NoxusIcon icon={iconName} size="smd" />
            ) : (
              <div
                className="w-5 h-5 rounded flex items-center justify-center relative p-[3px] shrink-0"
                style={{
                  backgroundColor: nodeData?.color || "#f3f4f6",
                }}
              >
                {nodeImage ? (
                  <div className="relative w-full h-full">
                    <ImageWithFallback
                      fill
                      style={{
                        objectFit: "contain",
                        backgroundColor: "transparent",
                      }}
                      alt={`${nodeData?.title} logo`}
                      src={nodeImage}
                    />
                  </div>
                ) : (
                  <Circle className="w-3 h-3 text-gray-600" />
                )}
              </div>
            )}
            <div className="flex-1 min-w-0">
              <div className="text-sm text-foreground/90 truncate">
                {node.node_config?.label ||
                  node.name ||
                  nodeData?.title ||
                  node.type}
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}

export function WorkflowRunTool({
  metadata,
  state,
  input,
  output,
  groupId,
  toolCallId,
  isPublic,
  callProviderMetadata,
}: WorkflowRunToolProps) {
  const { hideWorkflowProgress } = useChatDisplayOptionsContext();
  const { data: runData } = useRemoteData(
    DATA_KEYS.RUN,
    {
      path: {
        run_id: metadata?.run_id ?? "",
        group_id: groupId,
        workflow_id: metadata?.workflow_id ?? "",
      },
    },
    {
      enabled: !!metadata?.run_id && !!groupId && !!metadata?.workflow_id,
      refetchOnMount: false,
      retry: 100,
      retryDelay: 1000,
      refetchInterval: (query) => {
        if (query.state.error) return 750;
        const data = query.state.data;
        return !data ||
          data.status === "running" ||
          data.status === "queued" ||
          data.status === "stopped"
          ? 750
          : false;
      },
      refetchOnWindowFocus: false,
    }
  );

  // The workflow's own icon (as in the flows list), so the row reads as that
  // specific workflow rather than a generic flow glyph.
  const { data: workflow } = useRemoteData(
    DATA_KEYS.WORKFLOWS_ID,
    {
      path: { group_id: groupId, workflow_id: metadata?.workflow_id ?? "" },
    },
    {
      enabled: !!metadata?.workflow_id && !!groupId,
      refetchOnMount: false,
      refetchOnWindowFocus: false,
    }
  );
  // "spot:" icons are uploaded files served by an authenticated endpoint, so
  // public chats can't fetch them — fall back to the default workflow icon.
  // Preset icons are static assets and render fine either way.
  const rawWorkflowIcon = workflow?.meta?.icon;
  const workflowIcon =
    isPublic && rawWorkflowIcon?.startsWith("spot:")
      ? undefined
      : rawWorkflowIcon;

  const runFailed = runData?.status === "failed";
  const metadataError = callProviderMetadata?.noxus?.error === true;
  const hasError = runFailed || state === "error" || metadataError;
  const errorMessage = callProviderMetadata?.noxus?.error_message;

  const toolState: ToolState = hasError
    ? "error"
    : state === "output-available"
      ? "output-available"
      : "running";

  const part: ToolPartBase = {
    type: "tool-workflow",
    toolCallId: toolCallId,
    state: toolState,
    input: input,
    output: output,
  };

  const workflowName = metadata?.workflow_name || "Unknown Workflow";

  const showProgress = !hideWorkflowProgress && !hasError && metadata?.run_id;

  const customContent = showProgress ? (
    <WorkflowProgress runData={runData} />
  ) : hasError && errorMessage ? (
    <div className="px-3 py-2 text-sm text-destructive">{errorMessage}</div>
  ) : undefined;

  const info: ToolDisplayInfo = {
    // The workflow's own icon (as in the flows list / pickers), falling back to
    // the canonical workflow icon when the workflow has none stored.
    icon: <NoxusIcon icon={workflowIcon || "workflow"} size="xsm" />,
    messageStart: metadata?.display_message_start || `Running ${workflowName}`,
    messageEnd: metadata?.display_message_end || `${workflowName} completed`,
    messageError: `Workflow "${workflowName}" failed`,
    chainable: true,
    headerLabel: "run_workflow",
    renderer: "workflow",
  };

  return <BaseTool part={part} info={info} customContent={customContent} />;
}
